import React, { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Clock, Loader2, Minus, Plus, Sparkles, Users } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        api.get("/billing/plans")
            .then(({ data }) => setPlans(data.items || []))
            .catch(() => toast.error("Could not load plans"));
    }, []);

    if (user && !user.email_verified && user.plan_key === "pending") {
        return <Navigate to="/verify-email" replace />;
    }

    const pro = plans.find((p) => p.key === "professional");
    const team = plans.find((p) => p.key === "team");
    const trialAvailable = user && !user.trial_used && !user.on_trial
        && !["professional", "team"].includes(user.plan_key);

    async function startTrial(planKey) {
        try {
            setBusy(planKey);
            const { data } = await api.post("/trial/start", { plan_key: planKey });
            await refresh();
            toast.success(`Your 7-day ${planKey === "team" ? "Team" : "Professional"} trial is live, ${data.query_budget} credits included.`);
            navigate(planKey === "team" ? "/app/team" : "/app", { replace: true });
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not start the trial");
            setBusy(null);
        }
    }

    async function buyNow(planKey) {
        try {
            setBusy(`buy-${planKey}`);
            const { data } = await api.post("/billing/checkout", {
                plan_key: planKey,
                cycle: "monthly",
                seats: planKey === "team" ? 3 : 1,
            });
            if (data.external && /^https?:/.test(data.hosted_url || "")) {
                window.location.href = data.hosted_url;
                return;
            }
            navigate(`/app/billing/checkout/${data.id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail?.message || "Could not start checkout");
            setBusy(null);
        }
    }

    return (
        <div className="container-edit py-10 lg:py-16 max-w-4xl" data-testid="onboarding-page">
            <div className="label-eyebrow">Welcome{user?.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
            <h1 className="font-display text-3xl lg:text-5xl mt-2 tracking-tight">
                {trialAvailable ? "Start your 7-day free trial." : "Choose your plan."}
            </h1>
            <p className="mt-4 max-w-xl text-[#0D1B3E]/75">
                {trialAvailable
                    ? "Full platform access, no credit card required. Your trial includes 15 credits, 2 document uploads, and every research module."
                    : "Pick the plan that fits your practice. You can change or cancel any time."}
            </p>

            <div className="mt-10 grid gap-6 md:grid-cols-2">
                {/* Professional */}
                <div className="card-flat p-7 flex flex-col border-[#C9A84C] bg-[#FFFDF7]" data-testid="onboarding-card-professional">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} color="#C9A84C" />
                        <div className="font-display text-2xl tracking-tight">Professional</div>
                    </div>
                    <p className="mt-3 text-sm text-[#0D1B3E]/75">
                        The full research desk for individual practitioners.
                    </p>
                    <hr className="gold-rule my-5" />
                    <ul className="space-y-2 text-sm flex-1">
                        {trialAvailable ? (
                            <>
                                <Li>7 days free, then €{pro?.price_eur ?? 49}/month if you subscribe</Li>
                                <Li>15 trial credits (heavy features credit-weighted)</Li>
                                <Li>2 document uploads during the trial</Li>
                                <Li>All research + compliance modules</Li>
                            </>
                        ) : (
                            <>
                                <Li>€{pro?.price_eur ?? 49}/month</Li>
                                <Li>{(pro?.monthly_queries ?? 300).toLocaleString()} monthly credits</Li>
                                <Li>Up to {pro?.max_uploads ?? 30} uploads / month</Li>
                            </>
                        )}
                    </ul>
                    {trialAvailable && (
                        <button
                            type="button"
                            onClick={() => startTrial("professional")}
                            disabled={!!busy}
                            className="btn-primary w-full mt-6"
                            data-testid="onboarding-trial-professional"
                        >
                            {busy === "professional" ? <Loader2 size={16} className="animate-spin" /> : "Start your free Pro trial"}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => buyNow("professional")}
                        disabled={!!busy}
                        className={`${trialAvailable ? "btn-secondary" : "btn-primary"} w-full mt-3`}
                        data-testid="onboarding-buy-professional"
                    >
                        {busy === "buy-professional" ? <Loader2 size={16} className="animate-spin" /> : `Subscribe now · €${pro?.price_eur ?? 49}/mo`}
                    </button>
                </div>

                {/* Team */}
                <div className="card-flat p-7 flex flex-col" data-testid="onboarding-card-team">
                    <div className="flex items-center gap-2">
                        <Users size={18} color="#C9A84C" />
                        <div className="font-display text-2xl tracking-tight">Team</div>
                    </div>
                    <p className="mt-3 text-sm text-[#0D1B3E]/75">
                        A shared workspace with pooled credits and seat management.
                    </p>
                    <hr className="gold-rule my-5" />
                    <ul className="space-y-2 text-sm flex-1">
                        {trialAvailable ? (
                            <>
                                <Li>7 days free, then from €{team?.price_eur ?? 199}/month</Li>
                                <Li>You + up to 2 invited colleagues during the trial</Li>
                                <Li>15 pooled trial credits, shared watches</Li>
                                <Li>Argument Generator + Comparative Law</Li>
                            </>
                        ) : (
                            <>
                                <Li>From €{team?.price_eur ?? 199}/month · {team?.included_seats ?? 3} seats included</Li>
                                <Li>{(team?.monthly_queries ?? 1500).toLocaleString()}+ pooled monthly credits</Li>
                                <Li>Member invites, roles + usage tracking</Li>
                            </>
                        )}
                    </ul>
                    {trialAvailable && (
                        <button
                            type="button"
                            onClick={() => startTrial("team")}
                            disabled={!!busy}
                            className="btn-primary w-full mt-6"
                            data-testid="onboarding-trial-team"
                        >
                            {busy === "team" ? <Loader2 size={16} className="animate-spin" /> : "Start your free Team trial"}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => navigate("/app/pricing")}
                        disabled={!!busy}
                        className="btn-secondary w-full mt-3"
                        data-testid="onboarding-buy-team"
                    >
                        Configure seats & subscribe
                    </button>
                </div>
            </div>

            <div className="mt-10 flex items-center gap-2 text-sm text-[#0D1B3E]/60">
                <Clock size={14} />
                <span>
                    {trialAvailable
                        ? "One trial per account. When it ends, your saved work stays. Upgrade any time."
                        : "Need help choosing? Compare everything on the pricing page."}
                </span>
            </div>
        </div>
    );
}

export function SeatStepper({ seats, setSeats, min, max, extraEur }) {
    return (
        <div className="mt-4 border border-[#0D1B3E]/15 bg-white/70 p-3" data-testid="seat-stepper">
            <div className="flex items-center justify-between gap-3">
                <span className="text-[11px] uppercase tracking-[0.2em] text-[#0D1B3E]/60">Seats</span>
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => setSeats(Math.max(min, seats - 1))}
                        className="w-8 h-8 border border-[#0D1B3E]/20 flex items-center justify-center hover:bg-[#0D1B3E]/5 disabled:opacity-40"
                        disabled={seats <= min}
                        data-testid="seat-stepper-minus"
                        aria-label="Remove a seat"
                    >
                        <Minus size={14} />
                    </button>
                    <span className="font-display text-xl w-8 text-center" data-testid="seat-stepper-count">{seats}</span>
                    <button
                        type="button"
                        onClick={() => setSeats(Math.min(max, seats + 1))}
                        className="w-8 h-8 border border-[#0D1B3E]/20 flex items-center justify-center hover:bg-[#0D1B3E]/5 disabled:opacity-40"
                        disabled={seats >= max}
                        data-testid="seat-stepper-plus"
                        aria-label="Add a seat"
                    >
                        <Plus size={14} />
                    </button>
                </div>
            </div>
            <p className="mt-2 text-[11px] text-[#0D1B3E]/55">
                {min} seats included · €{extraEur}/mo per extra seat
            </p>
        </div>
    );
}

function Li({ children }) {
    return (
        <li className="flex items-start gap-2.5">
            <Check size={15} color="#C9A84C" className="mt-0.5 shrink-0" />
            <span className="text-[#0D1B3E]/85">{children}</span>
        </li>
    );
}
